import { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { fileUrl } from "@/components/UploadButton";
import ProductCard from "@/components/ProductCard";

// URL slug → backend category value + content key prefix
const ROUTES = {
  "chopping-boards": { value: "chopping_board", key: "chopping" },
  "resin-platters":  { value: "resin_platter",  key: "resin" },
  "furniture":       { value: "furniture",      key: "furniture" },
  "miscellaneous":   { value: "miscellaneous",  key: "miscellaneous" },
};

export default function CategoryPage() {
  const { slug } = useParams();
  const meta = ROUTES[slug];
  const { content } = useContent();
  const [products, setProducts] = useState([]);
  const [sub, setSub] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!meta) return;
    setLoading(true);
    api.get(`/products?category=${meta.value}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [meta]);

  const subcats = useMemo(() => {
    const set = new Set();
    products.forEach((p) => { if (p.subcategory) set.add(p.subcategory); });
    return Array.from(set).sort();
  }, [products]);

  // Group products by subcategory for the "sub-section" presentation when no filter is active.
  const grouped = useMemo(() => {
    if (sub) return null;
    const groups = {};
    const noSub = [];
    products.forEach((p) => {
      if (p.subcategory) {
        if (!groups[p.subcategory]) groups[p.subcategory] = [];
        groups[p.subcategory].push(p);
      } else {
        noSub.push(p);
      }
    });
    return { groups, noSub };
  }, [products, sub]);

  if (!meta) return <Navigate to="/shop" replace />;

  const cats = content.categories || {};
  const k = meta.key;
  const heading = cats[`${k}_heading`] || "";
  const kicker = cats[`${k}_kicker`] || "";
  const intro = cats[`${k}_intro`] || "";
  const heroImg = cats[`${k}_image`] || "";

  const visible = sub ? products.filter((p) => p.subcategory === sub) : products;

  return (
    <div data-testid={`category-page-${slug}`}>
      {/* Hero */}
      <section className="relative min-h-[55vh] sm:min-h-[65vh] grain overflow-hidden flex items-end">
        {heroImg && (
          <img
            src={fileUrl(heroImg)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-45"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#E0F2F1] via-[#E0F2F1]/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pb-14 sm:pb-20 w-full">
          {kicker && <p className="smallcaps mb-6 fade-up">{kicker}</p>}
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tighter text-[#212121] leading-[0.95] fade-up" style={{ animationDelay: "0.1s" }}>
            {heading}
          </h1>
          {intro && (
            <p className="text-[#757575] max-w-2xl mt-6 leading-relaxed fade-up" style={{ animationDelay: "0.2s" }}>
              {intro}
            </p>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 sm:py-24">
        {/* Sub-category chip row */}
        {subcats.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-12" data-testid="category-subcategory-row">
            <button
              onClick={() => setSub("")}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                !sub ? "border-[#B87333] text-[#B87333] font-bold" : "border-[#B2DFDB] text-[#757575] hover:border-[#B87333]"
              }`}
              data-testid="category-sub-all"
            >
              All {heading}
            </button>
            {subcats.map((s) => (
              <button
                key={s}
                onClick={() => setSub(s)}
                data-testid={`category-sub-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                  sub === s ? "border-[#B87333] text-[#B87333] font-bold" : "border-[#B2DFDB] text-[#757575] hover:border-[#B87333]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-[#757575]">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center" data-testid="category-empty">
            <p className="text-[#757575] mb-6">No pieces here yet — David is still in the workshop.</p>
            <Link to="/custom-order" className="btn-secondary">Commission a piece →</Link>
          </div>
        ) : sub || !grouped || subcats.length === 0 ? (
          // Flat grid when a subcategory is selected or there are no subcategories
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {visible.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        ) : (
          // Grouped by sub-category when showing everything
          <div className="space-y-20">
            {Object.entries(grouped.groups).map(([name, items]) => (
              <div key={name} data-testid={`group-${name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-end justify-between mb-8 pb-4 border-b border-[#B2DFDB]">
                  <div>
                    <p className="smallcaps mb-2">{heading}</p>
                    <h2 className="font-display text-3xl sm:text-4xl text-[#212121]">{name}</h2>
                  </div>
                  <button
                    onClick={() => setSub(name)}
                    className="text-[#757575] hover:text-[#B87333] font-bold text-xs uppercase tracking-widest hidden sm:inline"
                  >
                    View all →
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                  {items.map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
              </div>
            ))}
            {grouped.noSub.length > 0 && (
              <div data-testid="group-other">
                <div className="flex items-end mb-8 pb-4 border-b border-[#B2DFDB]">
                  <div>
                    <p className="smallcaps mb-2">{heading}</p>
                    <h2 className="font-display text-3xl sm:text-4xl text-[#212121]">More pieces</h2>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                  {grouped.noSub.map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Cross-link to other categories */}
      <section className="border-t border-[#B2DFDB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 sm:py-24">
          <p className="smallcaps mb-6">Keep browsing</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(ROUTES)
              .filter(([s]) => s !== slug)
              .map(([s, m]) => (
                <Link
                  key={s}
                  to={`/category/${s}`}
                  className="border border-[#B2DFDB] hover:border-[#B87333] px-6 py-3 text-sm uppercase tracking-widest text-[#757575] hover:text-[#212121] transition-colors"
                  data-testid={`crosslink-${s}`}
                >
                  {cats[`${m.key}_heading`] || s}
                </Link>
              ))}
            <Link
              to="/shop"
              className="border border-[#B2DFDB] hover:border-[#B87333] px-6 py-3 text-sm uppercase tracking-widest text-[#757575] hover:text-[#212121] transition-colors"
            >
              All pieces →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
